import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { DraftSheet } from '../api/types'
import { cellLabel, colName, evaluateCell } from '../lib/formula'
import { copyText } from '../lib/clipboard'
import { clamp, fit, MAX_COLS, MAX_ROWS, MIN_COLS, MIN_ROWS, PAD_COLS, PAD_ROWS, pasteInto, rectOf, trimGrid } from '../lib/grid'
import type { Matrix, Size } from '../lib/grid'

/**
 * 草稿表格的网格。刻意做成"一个大号、能键盘走的全网格"，而不是通用电子表格：
 * 操作员要的是把一串域名敲进来、顺手 =SUM 一下，然后过一会儿在库里贴到入库页。
 *
 * 尺寸按"老人看得清"定：格子 42px 高、正文 18px、选中框 2px 实线。
 * 交互照 WPS 的肌肉记忆来：直接打字进格、方向键走格、Enter 下移、Tab 右移、
 * F2 改格、拖框多选、Delete 清空整块、Ctrl+C/X/V 块级复制剪切粘贴、
 * Ctrl+D 向下填充、Ctrl+Z/Y 撤销重做、Ctrl+F 查找、拖列边框改列宽。
 *
 * 刻意不做的（说清楚，免得以为是漏了）：合并单元格、条件格式、字体颜色、图表。
 * 那些要真正的排版引擎，引进来就是几 MB 依赖，而这张纸的用途是清单不是报表。
 */

const HISTORY = 60
const COL_W = 132
const COL_MIN = 56
const COL_MAX = 560

const WIDTH_KEY = 'ds.sheet.colwidths'

type Pos = { r: number; c: number }
type Rect = { r0: number; r1: number; c0: number; c1: number }

interface Props {
  sheet: DraftSheet
  onChange: (next: DraftSheet) => void
  /** 只读账号：可以翻格子看，但不能改，也不会触发保存 */
  disabled?: boolean
}

const collator = new Intl.Collator('zh-Hans-CN', { numeric: true, sensitivity: 'base' })

function asNum(s: string): number | null {
  const t = s.replace(/,/g, '').trim()
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Number(n.toPrecision(12)))
}

// 列宽是本机的显示偏好，不进服务器（服务器只存 name + cells）。
function readWidths(): Record<string, number[]> {
  try {
    const raw = localStorage.getItem(WIDTH_KEY)
    return raw ? (JSON.parse(raw) as Record<string, number[]>) : {}
  } catch {
    return {}
  }
}
function writeWidths(map: Record<string, number[]>) {
  try {
    localStorage.setItem(WIDTH_KEY, JSON.stringify(map))
  } catch {
    /* 存不下就这次会话里有效，草稿内容本身不受影响 */
  }
}

export function Spreadsheet({ sheet, onChange, disabled = false }: Props) {
  const [size, setSize] = useState<Size>(() => fit(sheet.cells ?? []))
  const [anchor, setAnchor] = useState<Pos>({ r: 0, c: 0 })
  const [cur, setCur] = useState<Pos>({ r: 0, c: 0 })
  const [editing, setEditing] = useState(false)
  const [buf, setBuf] = useState('')
  const [wrap, setWrap] = useState(false)
  const [findText, setFindText] = useState('')
  const [filterText, setFilterText] = useState('')
  const [hideEmpty, setHideEmpty] = useState(false)
  const [widths, setWidths] = useState<Record<string, number[]>>(readWidths)
  const [hist, setHist] = useState<{ past: Matrix[]; future: Matrix[] }>({ past: [], future: [] })
  const [msg, setMsg] = useState('')
  const [picked, setPicked] = useState<Set<number>>(() => new Set())
  const pickAnchor = useRef(0)

  const box = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const findRef = useRef<HTMLInputElement>(null)
  const dragging = useRef(false)
  const resize = useRef<{ c: number; x: number; w: number } | null>(null)
  // 我们上一次交给上层的内容。用它区分"自己写回来的"和"服务器换进来的"。
  const emitted = useRef<Matrix | null>(null)

  // 换标签页 = 换一份内容：光标回到 A1、撤销栈清空（跨页撤销会把人绕晕）。
  useEffect(() => {
    setAnchor({ r: 0, c: 0 })
    setCur({ r: 0, c: 0 })
    setEditing(false)
    setHist({ past: [], future: [] })
    setPicked(new Set())
  }, [sheet.name]) // eslint-disable-line react-hooks/exhaustive-deps

  // 网格尺寸跟着"外面换进来的内容"重算：自己写回去的那份不动。
  // 少了这一段，服务器载入 100 行时尺寸会停在初始的 24 行，编辑后按 24 行裁剪 = 丢数据。
  useEffect(() => {
    const cells = sheet.cells ?? []
    if (cells === emitted.current) return
    emitted.current = cells
    const next = fit(cells)
    setSize((s) => (s.rows === next.rows && s.cols === next.cols ? s : next))
  }, [sheet.cells])

  useEffect(() => {
    if (!msg) return
    const t = window.setTimeout(() => setMsg(''), 5000)
    return () => window.clearTimeout(t)
  }, [msg])

  const grid = useMemo(() => rectOf(sheet.cells ?? [], size.rows, size.cols), [sheet.cells, size])

  // 显示值全部走求值器；原始值留在网格里，换浏览器不会丢"怎么算出来的"。
  const shown = useMemo(() => grid.map((row, r) => row.map((_, c) => evaluateCell(grid, r, c))), [grid])

  /** 可见行：筛选与隐藏空行只改变"看到哪些行"，不动数据本身。 */
  const visible = useMemo(() => {
    const all = grid.map((_, r) => r)
    const q = filterText.trim().toLowerCase()
    if (!q && !hideEmpty) return all
    return all.filter((r) => {
      const joined = grid[r].join('\t').toLowerCase()
      if (hideEmpty && joined.trim() === '') return false
      if (q && !joined.includes(q)) return false
      return true
    })
  }, [grid, filterText, hideEmpty])

  const visibleSet = useMemo(() => new Set(visible), [visible])

  const range: Rect = useMemo(
    () => ({
      r0: Math.min(anchor.r, cur.r),
      r1: Math.max(anchor.r, cur.r),
      c0: Math.min(anchor.c, cur.c),
      c1: Math.max(anchor.c, cur.c),
    }),
    [anchor, cur],
  )

  const stats = useMemo(() => {
    const nums: number[] = []
    let filled = 0
    for (let r = range.r0; r <= range.r1; r++) {
      for (let c = range.c0; c <= range.c1; c++) {
        if ((grid[r]?.[c] ?? '') !== '') filled++
        const n = asNum(shown[r]?.[c]?.text ?? '')
        if (n !== null) nums.push(n)
      }
    }
    const sum = nums.reduce((a, b) => a + b, 0)
    return {
      span: (range.r1 - range.r0 + 1) * (range.c1 - range.c0 + 1),
      filled,
      numeric: nums.length,
      sum,
      avg: nums.length ? sum / nums.length : 0,
    }
  }, [grid, shown, range])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing, cur])

  // 键盘走格 / 查找定位时把目标格滚进视野（已在视野内则不动）。
  useEffect(() => {
    box.current?.querySelector<HTMLElement>('[data-active="1"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [cur, size])

  // 列宽拖拽：move/up 挂在 window 上，鼠标滑出表头也能继续拖。
  useEffect(() => {
    function onMove(e: MouseEvent) {
      const d = resize.current
      if (!d) return
      const col = clamp(d.w + (e.clientX - d.x), COL_MIN, COL_MAX)
      setWidths((prev) => {
        const arr = (prev[sheet.name] ?? []).slice()
        while (arr.length <= d.c) arr.push(COL_W)
        arr[d.c] = col
        return { ...prev, [sheet.name]: arr }
      })
    }
    function onUp() {
      if (!resize.current) return
      resize.current = null
      document.body.classList.remove('col-resizing')
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [sheet.name])

  useEffect(() => {
    writeWidths(widths)
  }, [widths])

  const ensure = useCallback((rows: number, cols: number) => {
    setSize((s) => {
      const next = {
        rows: clamp(Math.max(s.rows, rows), MIN_ROWS, MAX_ROWS),
        cols: clamp(Math.max(s.cols, cols), MIN_COLS, MAX_COLS),
      }
      return next.rows === s.rows && next.cols === s.cols ? s : next
    })
  }, [])

  /** 当前内容的一份稠密副本，可以顺手放大到 rows × cols。 */
  const snapshot = useCallback((rows?: number, cols?: number): Matrix => rectOf(grid, rows ?? size.rows, cols ?? size.cols), [grid, size])

  /** 所有写路径的唯一出口：先把改动前的内容压进撤销栈，再交给上层自动保存。 */
  const apply = useCallback(
    (next: Matrix) => {
      if (disabled) return
      const out = trimGrid(next)
      setHist((h) => ({ past: [...h.past, grid].slice(-HISTORY), future: [] }))
      emitted.current = out
      onChange({ ...sheet, cells: out })
    },
    [disabled, grid, onChange, sheet],
  )

  function undo() {
    if (!hist.past.length || disabled) return
    const prev = hist.past[hist.past.length - 1]
    setHist({ past: hist.past.slice(0, -1), future: [grid, ...hist.future].slice(0, HISTORY) })
    const out = trimGrid(prev)
    emitted.current = out
    onChange({ ...sheet, cells: out })
    setMsg('已撤销')
  }

  function redo() {
    if (!hist.future.length || disabled) return
    const next = hist.future[0]
    setHist({ past: [...hist.past, grid].slice(-HISTORY), future: hist.future.slice(1) })
    const out = trimGrid(next)
    emitted.current = out
    onChange({ ...sheet, cells: out })
    setMsg('已重做')
  }

  const commit = useCallback(
    (value: string, next?: Pos) => {
      if (disabled) return
      const g = snapshot()
      g[cur.r][cur.c] = value
      apply(g)
      setEditing(false)
      if (next) gotoCell(next)
      else box.current?.focus()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apply, cur, disabled, snapshot],
  )

  function begin(value: string) {
    if (disabled) return
    setBuf(value)
    setEditing(true)
  }

  function gotoCell(p: Pos, extend = false) {
    // 夹到服务端允许的绝对上限而不是当前网格边缘：夹在边缘会让人走到最后一行时"卡住"，
    // ensure 会顺着光标把网格再撑大 6 行 4 列。
    const q = { r: clamp(p.r, 0, MAX_ROWS - 1), c: clamp(p.c, 0, MAX_COLS - 1) }
    ensure(q.r + 6, q.c + 4)
    setCur(q)
    if (!extend) setAnchor(q)
  }

  /** 上下走格按「可见行」跳：筛选状态下不会掉进看不见的行里。 */
  function stepRow(dr: number): number {
    if (!visible.length) return cur.r
    if (dr > 0) {
      const i = visible.findIndex((r) => r > cur.r)
      return i < 0 ? cur.r : visible[i]
    }
    for (let i = visible.length - 1; i >= 0; i--) if (visible[i] < cur.r) return visible[i]
    return cur.r
  }

  function move(dr: number, dc: number, extend = false) {
    gotoCell({ r: dr ? stepRow(dr) : cur.r, c: cur.c + dc }, extend)
  }

  function clearRange() {
    const g = snapshot()
    let touched = false
    for (let r = range.r0; r <= range.r1; r++) {
      for (let c = range.c0; c <= range.c1; c++) {
        if (g[r][c] !== '') touched = true
        g[r][c] = ''
      }
    }
    if (touched) apply(g)
  }

  function fillDown() {
    if (disabled) return
    const g = snapshot()
    for (let c = range.c0; c <= range.c1; c++) {
      const v = g[range.r0][c]
      for (let r = range.r0 + 1; r <= range.r1; r++) g[r][c] = v
    }
    apply(g)
    setMsg('已把首行内容向下填充')
  }

  /** 一键求和：选中跨多行就在下方补一行 SUM，只跨多列则在右侧补一列 SUM。 */
  function autoSum() {
    if (disabled) return
    if (range.r0 === range.r1 && range.c0 === range.c1) return setMsg('先框住要求和的那些格子')
    if (range.r1 > range.r0) {
      const r = range.r1 + 1
      if (r >= MAX_ROWS) return setMsg(`已经到了 ${MAX_ROWS} 行上限，放不下合计行`)
      const g = snapshot(r + 1 + PAD_ROWS, size.cols)
      for (let c = range.c0; c <= range.c1; c++) g[r][c] = `=SUM(${cellLabel(range.r0, c)}:${cellLabel(range.r1, c)})`
      apply(g)
      ensure(r + 1, size.cols)
      setMsg(`已在第 ${r + 1} 行写入 SUM`)
      gotoCell({ r, c: range.c0 })
    } else {
      const c = range.c1 + 1
      if (c >= MAX_COLS) return setMsg(`已经到了 ${MAX_COLS} 列上限，放不下合计列`)
      const g = snapshot(size.rows, c + PAD_COLS)
      g[range.r0][c] = `=SUM(${cellLabel(range.r0, range.c0)}:${cellLabel(range.r0, c - 1)})`
      apply(g)
      ensure(size.rows, c + 1)
      setMsg(`已在 ${cellLabel(range.r0, c)} 写入 SUM`)
      gotoCell({ r: range.r0, c })
    }
  }

  /** 排序只动选中区域里「当前可见」的行：筛选中排的是眼睛看到的那部分。 */
  function sortRange(dir: number) {
    if (disabled) return
    const rows = visible.filter((r) => r >= range.r0 && r <= range.r1)
    if (rows.length < 2) return setMsg('至少框住两行才能排序')
    const g = snapshot()
    const block = rows.map((r) => g[r].slice(range.c0, range.c1 + 1))
    block.sort((a, b) => dir * compareCell(a[0] ?? '', b[0] ?? ''))
    rows.forEach((r, i) => {
      for (let c = range.c0; c <= range.c1; c++) g[r][c] = block[i][c - range.c0] ?? ''
    })
    apply(g)
    setMsg(dir > 0 ? '已按本列升序排列' : '已按本列降序排列')
  }

  function compareCell(a: string, b: string): number {
    if (a === b) return 0
    if (a === '') return 1
    if (b === '') return -1
    const na = asNum(a)
    const nb = asNum(b)
    if (na !== null && nb !== null) return na - nb
    return collator.compare(a, b)
  }

  function findNext() {
    const q = findText.trim().toLowerCase()
    if (!q) return
    const total = size.rows * size.cols
    const start = cur.r * size.cols + cur.c
    for (let i = 1; i <= total; i++) {
      const idx = (start + i) % total
      const r = Math.floor(idx / size.cols)
      if (!visibleSet.has(r)) continue
      if ((shown[r][idx % size.cols].text || '').toLowerCase().includes(q)) {
        gotoCell({ r, c: idx % size.cols })
        setMsg(`找到 ${cellLabel(r, idx % size.cols)}`)
        return
      }
    }
    setMsg(`没有找到包含「${findText.trim()}」的格子`)
  }

  function onKeyDown(e: React.KeyboardEvent) {
    // 编辑中把按键交给格子里的输入框（含浏览器原地的文本撤销）。
    if (editing) return
    const k = e.key
    const ctrl = e.ctrlKey || e.metaKey
    if (ctrl && k === 'f') {
      e.preventDefault()
      findRef.current?.focus()
      findRef.current?.select()
      return
    }
    if (ctrl && (k === 'z' || k === 'Z')) {
      e.preventDefault()
      if (e.shiftKey || k === 'Z') redo()
      else undo()
      return
    }
    if (ctrl && (k === 'y' || k === 'Y')) {
      e.preventDefault()
      redo()
      return
    }
    if ((ctrl || e.altKey) && (k === '=' || k === '+')) {
      e.preventDefault()
      autoSum()
      return
    }
    if (ctrl && k === 'd') {
      e.preventDefault()
      fillDown()
      return
    }
    if (k === 'ArrowUp') return (e.preventDefault(), move(-1, 0, e.shiftKey))
    if (k === 'ArrowDown') return (e.preventDefault(), move(1, 0, e.shiftKey))
    if (k === 'ArrowLeft') return (e.preventDefault(), move(0, -1, e.shiftKey))
    if (k === 'ArrowRight') return (e.preventDefault(), move(0, 1, e.shiftKey))
    if (k === 'Home') return (e.preventDefault(), gotoCell({ r: cur.r, c: 0 }, e.shiftKey))
    if (k === 'End') return (e.preventDefault(), gotoCell({ r: cur.r, c: size.cols - 1 }, e.shiftKey))
    if (k === 'PageUp') return (e.preventDefault(), gotoCell({ r: cur.r - 10, c: cur.c }, e.shiftKey))
    if (k === 'PageDown') return (e.preventDefault(), gotoCell({ r: cur.r + 10, c: cur.c }, e.shiftKey))
    if (k === 'Tab') return (e.preventDefault(), move(0, e.shiftKey ? -1 : 1))
    if (k === 'Enter' || k === 'F2') {
      e.preventDefault()
      begin(grid[cur.r][cur.c])
      return
    }
    if (k === 'Delete' || k === 'Backspace') {
      e.preventDefault()
      clearRange()
      return
    }
    // 直接开打就进入编辑：少一次"双击进入单元格"的动作。
    if (k.length === 1 && !ctrl && !e.altKey) {
      e.preventDefault()
      begin(k)
    }
  }

  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit(buf, { r: stepRow(1), c: cur.c })
    } else if (e.key === 'Tab') {
      e.preventDefault()
      commit(buf, { r: cur.r, c: clamp(cur.c + (e.shiftKey ? -1 : 1), 0, MAX_COLS - 1) })
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditing(false)
      box.current?.focus()
    }
  }

  function blockText(g: Matrix, useShown: boolean): string {
    const lines: string[] = []
    for (let r = range.r0; r <= range.r1; r++) {
      const cells: string[] = []
      for (let c = range.c0; c <= range.c1; c++) cells.push(useShown ? shown[r][c].text : g[r][c])
      lines.push(cells.join('\t'))
    }
    return lines.join('\n')
  }

  function onCopy(e: React.ClipboardEvent) {
    if (editing) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', blockText(grid, true))
  }

  function onCut(e: React.ClipboardEvent) {
    if (editing || disabled) return
    e.preventDefault()
    e.clipboardData.setData('text/plain', blockText(grid, false))
    clearRange()
    setMsg('已剪切，Ctrl+V 贴到别处')
  }

  function onPaste(e: React.ClipboardEvent) {
    if (disabled) return
    const text = e.clipboardData.getData('text/plain')
    if (!text) return
    e.preventDefault()
    const { grid: g, rows, cols, droppedRows, droppedCols } = pasteInto(grid, cur.r, cur.c, text)
    if (!rows) return
    apply(g)
    // 贴到网格边缘时顺带留一屏余量：不然最后一行就是边界，接着往下敲要先按一次方向键。
    const grew = g.length > size.rows || g[0].length > size.cols
    ensure(grew ? g.length + PAD_ROWS : g.length, grew ? g[0].length + PAD_COLS : g[0].length)
    setAnchor({ r: Math.min(g.length - 1, cur.r + rows - 1), c: Math.min(g[0].length - 1, cur.c + cols - 1) })
    setMsg(
      droppedRows > 0
        ? `只贴下前 ${Math.min(rows, MAX_ROWS - cur.r)} 行，超出的 ${droppedRows} 行在 ${MAX_ROWS} 行上限以外`
        : droppedCols > 0
          ? `已贴上 ${rows} 行，但每行只留下前 ${MAX_COLS} 列`
          : `已贴上 ${rows} 行`,
    )
  }

  const raw = grid[cur.r][cur.c]
  const curShown = shown[cur.r][cur.c]
  const colWidths = widths[sheet.name] ?? []
  const filtered = visible.length !== grid.length

  function toggleRow(r: number, shift: boolean) {
    setPicked((prev) => {
      const next = new Set(prev)
      if (shift) {
        const a = Math.min(pickAnchor.current, r)
        const b = Math.max(pickAnchor.current, r)
        for (let i = a; i <= b; i++) next.add(i)
      } else if (next.has(r)) next.delete(r)
      else next.add(r)
      return next
    })
    if (!shift) pickAnchor.current = r
  }

  async function copyPicked() {
    const rows = [...picked].sort((a, b) => a - b)
    const lines: string[] = []
    for (const r of rows) {
      const cells = grid[r] ?? []
      const filled = cells.map((cell) => cell.trim()).filter(Boolean)
      if (filled.length) lines.push(filled.join('\t'))
    }
    if (!lines.length) {
      setMsg(picked.size ? '勾选的行都是空的' : '先点左侧行号勾选，再复制')
      return
    }
    const ok = await copyText(lines.join('\n'))
    setMsg(ok ? `已复制 ${lines.length} 行` : '复制失败，浏览器没给剪贴板权限')
  }

  function cellClass(r: number, c: number): string {
    const active = cur.r === r && cur.c === c
    const inRange = r >= range.r0 && r <= range.r1 && c >= range.c0 && c <= range.c1
    const cell = shown[r][c]
    const formula = grid[r][c].startsWith('=')
    return [
      active ? 'active' : '',
      inRange && !active ? 'sel' : '',
      cell.error ? 'err' : '',
      formula && !active ? 'fx' : '',
      /^[0-9.]+$/.test(cell.text) && !formula ? 'num' : '',
    ].join(' ')
  }

  return (
    <div className="sheet-wrap">
      <div className="sheet-tools">
        <div className="sheet-tool-group">
          <button
            type="button"
            className="primary"
            onClick={() => void copyPicked()}
            disabled={!picked.size}
            title="点左侧行号勾选多行，Shift 连选。只复制有内容的格子，一行一条"
          >
            一键复制（已选项）
          </button>
          <button type="button" onClick={undo} disabled={disabled || !hist.past.length} title="撤销上一步（Ctrl+Z）">
            撤销
          </button>
          <button type="button" onClick={redo} disabled={disabled || !hist.future.length} title="重做（Ctrl+Y）">
            重做
          </button>
          <button type="button" onClick={autoSum} disabled={disabled} title="框住一列数字再点，在它下面写一个 SUM（Ctrl+=）">
            一键求和
          </button>
          <button type="button" onClick={fillDown} disabled={disabled} title="把选区第一行的内容填到下面各行（Ctrl+D）">
            向下填充
          </button>
          <button type="button" onClick={() => sortRange(1)} disabled={disabled} title="按当前列把选区升序排列">
            升序
          </button>
          <button type="button" onClick={() => sortRange(-1)} disabled={disabled} title="按当前列把选区降序排列">
            降序
          </button>
          <label className="sheet-check" title="格子写不下时换行显示，不再截断">
            <input type="checkbox" checked={wrap} onChange={(e) => setWrap(e.target.checked)} />
            自动换行
          </label>
        </div>
        <div className="sheet-tool-group">
          <input
            ref={findRef}
            value={findText}
            onChange={(e) => setFindText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                findNext()
              } else if (e.key === 'Escape') {
                setFindText('')
                box.current?.focus()
              }
            }}
            placeholder="查找内容"
            aria-label="查找内容"
          />
          <button type="button" onClick={findNext} disabled={!findText.trim()} title="跳到下一个匹配的格子（Ctrl+F）">
            查找下一个
          </button>
          <input
            className="sheet-filter"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="筛选：只看到包含…"
            aria-label="筛选：只显示包含以下文字的行"
          />
          <label className="sheet-check">
            <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
            隐藏空行
          </label>
          {(filterText || hideEmpty) && (
            <button
              type="button"
              className="link"
              onClick={() => {
                setFilterText('')
                setHideEmpty(false)
              }}
            >
              取消筛选
            </button>
          )}
        </div>
      </div>

      <div className="sheet-fx">
        <span className="sheet-fx-label">{cellLabel(cur.r, cur.c)}</span>
        <input
          value={editing ? buf : raw}
          onChange={(e) => {
            if (!editing) begin(e.target.value)
            else setBuf(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit(editing ? buf : e.currentTarget.value, { r: stepRow(1), c: cur.c })
            }
          }}
          placeholder="内容，或以 = 开头的公式（=SUM(A1:A8)、=A1&amp;&quot;.&quot;&amp;A2）"
          aria-label="编辑当前单元格"
        />
        {curShown.error && (
          <span className="sheet-fx-err" role="alert">
            {curShown.error}
          </span>
        )}
      </div>

      <div
        className={'sheet-grid' + (wrap ? ' wrap' : '')}
        ref={box}
        tabIndex={0}
        role="grid"
        aria-label={`${sheet.name} 工作表`}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onCopy={onCopy}
        onCut={onCut}
        onMouseUp={() => {
          dragging.current = false
        }}
        onMouseLeave={() => {
          dragging.current = false
        }}
      >
        <table>
          <colgroup>
            <col style={{ width: 52 }} />
            {Array.from({ length: size.cols }, (_, c) => (
              <col key={c} style={{ width: colWidths[c] ?? COL_W }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className="corner" aria-hidden="true" />
              {Array.from({ length: size.cols }, (_, c) => (
                <th key={c} className={cur.c === c ? 'on' : ''}>
                  <span className="col-name">{colName(c)}</span>
                  <span
                    className="col-resize"
                    role="separator"
                    aria-orientation="vertical"
                    title="按住拖动改列宽，双击回到默认宽度"
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      resize.current = { c, x: e.clientX, w: colWidths[c] ?? COL_W }
                      document.body.classList.add('col-resizing')
                    }}
                    onDoubleClick={() => {
                      setWidths((prev) => {
                        const arr = (prev[sheet.name] ?? []).slice()
                        while (arr.length <= c) arr.push(COL_W)
                        arr[c] = COL_W
                        return { ...prev, [sheet.name]: arr }
                      })
                    }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r} className={picked.has(r) ? 'picked' : undefined}>
                <th
                  className={cur.r === r ? 'on' : ''}
                  title="点一下勾选这行，再点取消。按住 Shift 连选"
                  onMouseDown={(e) => {
                    if (e.button !== 0) return
                    e.preventDefault()
                    e.stopPropagation()
                    toggleRow(r, e.shiftKey)
                  }}
                >
                  {r + 1}
                </th>
                {Array.from({ length: size.cols }, (_, c) => {
                  const active = cur.r === r && cur.c === c
                  return (
                    <td
                      key={c}
                      data-active={active ? '1' : undefined}
                      data-cell={cellLabel(r, c)}
                      className={cellClass(r, c)}
                      onMouseDown={(e) => {
                        if (e.button !== 0) return
                        setEditing(false)
                        dragging.current = true
                        setCur({ r, c })
                        if (!e.shiftKey) setAnchor({ r, c })
                        box.current?.focus()
                      }}
                      onMouseEnter={() => {
                        if (dragging.current) setCur({ r, c })
                      }}
                      onDoubleClick={() => {
                        setCur({ r, c })
                        begin(grid[r][c])
                      }}
                      aria-selected={active}
                    >
                      {active && editing ? (
                        <input
                          ref={inputRef}
                          value={buf}
                          onChange={(e) => setBuf(e.target.value)}
                          onKeyDown={onInputKeyDown}
                          onBlur={() => commit(buf)}
                          aria-label={cellLabel(r, c)}
                        />
                      ) : (
                        <span>{shown[r][c].text}</span>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sheet-status">
        <span className="sheet-stats" role="status">
          {picked.size
            ? `已勾选 ${picked.size} 行`
            : stats.span > 1
              ? `选中 ${stats.span} 格 · 已填 ${stats.filled} · 数字 ${stats.numeric} 个 · 合计 ${fmtNum(stats.sum)}${
                  stats.numeric ? ` · 平均 ${fmtNum(stats.avg)}` : ''
                }`
              : `选中 ${cellLabel(cur.r, cur.c)}`}
        </span>
        {filtered && (
          <span className="muted">
            筛选中：{visible.length} / {grid.length} 行（只是没显示，数据都还在）
          </span>
        )}
        {msg && <span className="sheet-msg">{msg}</span>}
        <span className="muted grow">拖动列边框改列宽，双击边框复原</span>
      </div>
    </div>
  )
}
